import * as React from "react";
import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

const AdminRoadmapSmoke = React.lazy(
  () => import("./pages/admin/AdminRoadmapSmoke"),
);

const ExplorePublicRoadmap = React.lazy(
  () => import("./pages/roadmap/ExplorePublicRoadmap"),
);

import { initDataSource } from "./lib/datasource";
import { ToastProvider } from "./components/ui/toast";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./auth/AuthProvider";
import EnvErrorCard from "./components/common/EnvErrorCard";
import { RouteErrorBoundary } from "./components/system/RouteErrorBoundary";
import RequireAdmin from "./components/auth/RequireAdmin";
import ProtectedRoute from "./routes/ProtectedRoute";

// Layouts
import LoggedInLayout from "./layouts/LoggedInLayout";
import AdminLayout from "./layouts/AdminLayout";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Pages
import LandingPage from "./routes/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import DashboardPage from "./routes/DashboardPage";
import CreateDealPage from "./routes/CreateDealPage";
import UnderwritingHome from "./routes/UnderwritingHome";
import DealWorkspace from "./routes/DealWorkspace";
import PropertiesPage from "./routes/PropertiesPage";
import MembershipPage from "./routes/MembershipPage";
import LegalPrivacyPage from "./routes/LegalPrivacyPage";
import LegalTermsPage from "./routes/LegalTermsPage";
import ChartsKPIsPage from "./pages/ChartsKPIsPage";
import StaffingSenseCheckPage from "./pages/StaffingSenseCheckPage";
import UnderwritingSummaryPage from "./pages/UnderwritingSummaryPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminDealsPage from "./pages/admin/AdminDealsPage";
import AdminDealInspectPage from "./pages/admin/AdminDealInspectPage";
import AdminBenchmarksPage from "./pages/admin/AdminBenchmarksPage";
import AdminRoadmapPage from "./pages/admin/AdminRoadmapPage";
import UserRoadmapLanding from "./pages/roadmap/UserRoadmapLanding";
import RoadmapProjectPage from "./pages/roadmap/UserRoadmapPage";
import ConsultancyRequestPage from "./features/consulting/ConsultancyRequestPage";
import AdminConsultingPage from "./features/consulting/AdminConsultingPage";
import DebugEnv from "./pages/DebugEnv";
import AuthDebug from "./pages/auth/AuthDebug";
import AdminDeletionListPage from "./pages/admin/AdminDeletionList";
import Templates from "./pages/Templates";
import Partners from "./pages/Partners";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminTemplates from "./pages/admin/AdminTemplates";
import DealEditor from "./components/deal/DealEditor";
import AdminPayroll from "./pages/admin/AdminPayroll";
import AdminDealsDetailPage from "./pages/admin/AdminDealsDetailPage";
import NotFound from "./pages/NotFound";
import ModalManager from "./components/auth/ModalManager";
import AdminExcelTemplate from "./pages/admin/AdminExcelTemplate";

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  const [envError, setEnvError] = useState<string | null>(null);

  useEffect(() => {
    initDataSource().catch((error) => {
      if (error.message === "SUPABASE_MISSING_CONFIG") {
        setEnvError("SUPABASE_MISSING_CONFIG");
      } else {
        console.error("DataSource init failed:", error);
      }
    });
  }, []);

  if (envError === "SUPABASE_MISSING_CONFIG") {
    return <EnvErrorCard />;
  }

  return (
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <ScrollToTop />
              <RouteErrorBoundary>
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                    </div>
                  }
                >
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <PublicLayout>
                          <LandingPage />
                        </PublicLayout>
                      }
                    />
                    <Route
                      path="/properties"
                      element={
                        <PublicLayout>
                          <PropertiesPage />
                        </PublicLayout>
                      }
                    />
                    <Route
                      path="/membership"
                      element={
                        <PublicLayout>
                          <MembershipPage />
                        </PublicLayout>
                      }
                    />
                    <Route
                      path="/legal/privacy"
                      element={
                        <PublicLayout>
                          <LegalPrivacyPage />
                        </PublicLayout>
                      }
                    />
                    <Route
                      path="/legal/terms"
                      element={
                        <PublicLayout>
                          <LegalTermsPage />
                        </PublicLayout>
                      }
                    />

                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/reset" element={<ResetPasswordPage />} />

                    <Route element={<ProtectedRoute />}>
                      <Route
                        path="/dashboard"
                        element={
                          <LoggedInLayout title="Dashboard">
                            <DashboardPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/underwriting"
                        element={
                          <LoggedInLayout title="Underwriting">
                            <UnderwritingHome />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/templates"
                        element={
                          <LoggedInLayout title="Templates">
                            <Templates />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/partners"
                        element={
                          <LoggedInLayout title="Partners">
                            <Partners />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/underwriting/create"
                        element={
                          <LoggedInLayout title="Create New Deal">
                            <CreateDealPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/underwriting/:id"
                        element={
                          <LoggedInLayout>
                            <DealWorkspace />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/analysis/charts/:id"
                        element={
                          <LoggedInLayout title="Charts & Key KPIs">
                            <ChartsKPIsPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/analysis/staffing/:id"
                        element={
                          <LoggedInLayout title="Staffing Sense Check">
                            <StaffingSenseCheckPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/underwriting/summary/:id"
                        element={
                          <LoggedInLayout title="Underwriting Summary">
                            <UnderwritingSummaryPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/roadmap/:projectId"
                        element={
                          <LoggedInLayout title="Development Roadmap">
                            <RoadmapProjectPage mode="project" />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/consultancy"
                        element={
                          <LoggedInLayout title="Hotel Consulting">
                            <ConsultancyRequestPage />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/roadmap"
                        element={
                          <LoggedInLayout title="Development Roadmap">
                            <UserRoadmapLanding />
                          </LoggedInLayout>
                        }
                      />
                      <Route
                        path="/roadmap/explore"
                        element={
                          <LoggedInLayout title="Development Roadmap">
                            <Suspense
                              fallback={
                                <div className="min-h-screen flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                                </div>
                              }
                            >
                              <ExplorePublicRoadmap />
                            </Suspense>
                          </LoggedInLayout>
                        }
                      />
                    </Route>

                    <Route
                      path="/admin"
                      element={
                        <RequireAdmin>
                          <AdminLayout />
                        </RequireAdmin>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to="/admin/overview" replace />}
                      />
                      <Route
                        path="users/projects"
                        element={<AdminDealsDetailPage />}
                      />
                      <Route path="overview" element={<AdminOverview />} />

                      <Route
                        path="benchmarks"
                        element={<AdminBenchmarksPage />}
                      />
                      <Route path="users" element={<AdminUsersPage />} />
                      <Route
                        path="deletion-requests"
                        element={<AdminDeletionListPage />}
                      />
                      <Route path="deals" element={<AdminDealsPage />} />
                      <Route
                        path="deals/:dealId/inspect"
                        element={<AdminDealInspectPage />}
                      />
                      <Route path="roadmap" element={<AdminRoadmapPage />} />
                      <Route
                        path="roadmap/smoke"
                        element={
                          <Suspense
                            fallback={
                              <div className="p-6 text-sm">
                                Loading smoke page…
                              </div>
                            }
                          >
                            <AdminRoadmapSmoke />
                          </Suspense>
                        }
                      />

                      <Route
                        path="consulting"
                        element={<AdminConsultingPage />}
                      />
                      <Route path="partners" element={<AdminPartners />} />
                      <Route
                        path="deals/create"
                        element={<DealEditor mode="create" />}
                      />
                      <Route
                        path="deals/edit/:id"
                        element={<DealEditor mode="edit" />}
                      />
                      {/* <Route
                        path="deals/view/:id"
                        element={<DealEditor mode="view" />}
                      /> */}
                      <Route path="templates" element={<AdminTemplates />} />
                      <Route path="payroll-config" element={<AdminPayroll />} />
                      <Route
                        path="excel-template"
                        element={<AdminExcelTemplate />}
                      />
                    </Route>

                    {/* Redirects */}
                    <Route
                      path="/home"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="/billing/*"
                      element={<Navigate to="/membership" replace />}
                    />

                    {/* Debug Route */}
                    <Route path="/debug" element={<DebugEnv />} />
                    <Route path="/auth/debug" element={<AuthDebug />} />

                    {/* 404 */}
                    <Route
                      path="*"
                      element={
                        <PublicLayout>
                          <NotFound />
                        </PublicLayout>
                      }
                    />
                  </Routes>
                  <Suspense fallback={null}>
                    <ModalManager />
                  </Suspense>
                </Suspense>
              </RouteErrorBoundary>
              <Toaster />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  );
}

export default App;
